/**
 * Photos and scanned pages read by the vision model (docs/vision-ocr.md): the mapper of
 * the `photo` block of an attachment and the rows of the "From the photo" panel.
 *
 * Everything read from a picture is evidence with a status and a place, never a fact.
 * The mapper takes untrusted JSON and never throws: unknown kinds and statuses fall back
 * to the least sure reading (`unclear` for a code, `read_once` for a quantity), texts are
 * capped, and a missing status never turns into "agreed".
 *
 * Pure functions, no React, so every rule here is tested in `tests/photo.test.mjs`.
 */

import type {
  AttachmentRead,
  PhotoItemRead,
  PhotoKind,
  PhotoMachineField,
  PhotoMachineRead,
  PhotoPlace,
  PhotoReading,
  PhotoReadStatus,
  PhotoReferenceField,
  PhotoReferenceRead,
  PhotoValueUse,
} from "@/lib/api";
import {
  PHOTO_FIELD_LABELS,
  PHOTO_MACHINE_USE,
  PHOTO_REFERENCE_USE,
  PHOTO_SALES_ORDER_USE,
  photoUsedForText,
} from "@/lib/work-labels";

const KINDS: readonly PhotoKind[] = [
  "nameplate",
  "part_photo",
  "label",
  "handwritten_list",
  "scanned_form",
  "drawing",
  "screenshot",
  "other",
];
const STATUSES: readonly PhotoReadStatus[] = ["agreed", "read_once", "unclear"];
const READ_STATES: readonly NonNullable<PhotoReading["status"]>[] = [
  "read",
  "unclear",
  "rejected",
  "failed",
];
const USES: readonly PhotoValueUse[] = [
  "suggestion",
  "candidates",
  "needs_pick",
  "not_looked_up",
  "mail_line",
  "reference",
  "no_line",
  "not_asked",
];
const MACHINE_FIELDS: readonly PhotoMachineField[] = ["make", "type", "serial", "year"];
const REFERENCE_FIELDS: readonly PhotoReferenceField[] = [
  "drawing_no",
  "customer_ref",
  "po",
  "sales_order",
];

/** The caps of the reading schema (§2.3): 40 items, 200 characters per text. */
export const MAX_PHOTO_ITEMS = 40;
const MAX_REFERENCES = 8;
const MAX_TEXT = 200;
const MAX_LINE = 999;

/** The confidence of a status when the server sent none (§2.4: 0.8, 0.4, 0.1). */
export const STATUS_CONFIDENCE: Record<PhotoReadStatus, number> = {
  agreed: 0.8,
  read_once: 0.4,
  unclear: 0.1,
};

function isRecord(raw: unknown): raw is Record<string, unknown> {
  return raw != null && typeof raw === "object" && !Array.isArray(raw);
}

function oneOf<T extends string>(values: readonly T[], raw: unknown): T | null {
  if (typeof raw !== "string") {
    return null;
  }
  const lowered = raw.trim().toLowerCase();
  return (values as readonly string[]).includes(lowered) ? (lowered as T) : null;
}

/** A trimmed, capped text; null for anything else. */
function text(raw: unknown, max = MAX_TEXT): string | null {
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return String(raw);
  }
  if (typeof raw !== "string") {
    return null;
  }
  const trimmed = raw.replace(/\s+/g, " ").trim();
  if (trimmed === "") {
    return null;
  }
  return trimmed.length > max ? `${trimmed.slice(0, max - 1)}…` : trimmed;
}

function count(raw: unknown): number {
  return typeof raw === "number" && Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 0;
}

/** A whole number from 1 to `max`, else null. */
function ordinal(raw: unknown, max = MAX_LINE): number | null {
  return typeof raw === "number" && Number.isInteger(raw) && raw >= 1 && raw <= max ? raw : null;
}

/** A plain quantity up to 10,000,000 (the shape check of §2.4), else null. */
function quantity(raw: unknown): number | null {
  return typeof raw === "number" && Number.isFinite(raw) && raw > 0 && raw <= 10_000_000
    ? raw
    : null;
}

/** 0..1, else null. */
function unit(raw: unknown): number | null {
  return typeof raw === "number" && Number.isFinite(raw) && raw >= 0 && raw <= 1 ? raw : null;
}

/** Four numbers 0..1 with x0 < x1 and y0 < y1, else null. */
export function toBox(raw: unknown): [number, number, number, number] | null {
  if (!Array.isArray(raw) || raw.length !== 4) {
    return null;
  }
  const [x0, y0, x1, y1] = raw.map(unit);
  if (x0 == null || y0 == null || x1 == null || y1 == null) {
    return null;
  }
  return x0 < x1 && y0 < y1 ? [x0, y0, x1, y1] : null;
}

function toPlace(raw: unknown): PhotoPlace | null {
  if (!isRecord(raw)) {
    return null;
  }
  const parts = ordinal(raw.parts, 16);
  const part = ordinal(raw.part, 16);
  const place: PhotoPlace = {
    image: ordinal(raw.image, 99),
    page: ordinal(raw.page, 99),
    // A part beyond the number of parts is not a place the preview can show.
    part: part !== null && (parts === null || part <= parts) ? part : null,
    parts,
    box: toBox(raw.box),
    part_box: toBox(raw.part_box),
  };
  return place.image === null && place.page === null && place.part === null && place.box === null
    ? null
    : place;
}

/** A code status: anything unknown is the least sure one. */
function toStatus(raw: unknown): PhotoReadStatus {
  return oneOf(STATUSES, raw) ?? "unclear";
}

function toItem(raw: unknown): PhotoItemRead | null {
  if (!isRecord(raw)) {
    return null;
  }
  const code = text(raw.code, 60);
  const description = text(raw.description);
  const qty = quantity(raw.qty);
  if (!code && !description && qty === null) {
    return null;
  }
  // A code with an unreadable character is unclear whatever the server says.
  const status = code && code.includes("?") ? "unclear" : toStatus(raw.status);
  return {
    code,
    status,
    confidence: unit(raw.confidence),
    qty,
    qty_status: qty === null ? null : (oneOf(STATUSES, raw.qty_status) ?? "read_once"),
    unit: text(raw.unit, 20),
    description,
    marked: raw.marked === true,
    position: text(raw.position, 20),
    // The place nested (`place`) or flat on the item (`part`, `box`), as the reader stores it.
    place: toPlace(raw.place) ?? toPlace(raw),
    line: ordinal(raw.line),
    use: oneOf(USES, raw.use),
    sentence: text(raw.sentence, 240),
  };
}

function toMachine(raw: unknown): PhotoMachineRead | null {
  if (!isRecord(raw)) {
    return null;
  }
  const statuses: Partial<Record<PhotoMachineField, PhotoReadStatus>> = {};
  if (isRecord(raw.statuses)) {
    for (const field of MACHINE_FIELDS) {
      const status = oneOf(STATUSES, raw.statuses[field]);
      if (status) {
        statuses[field] = status;
      }
    }
  }
  const places: Partial<Record<PhotoMachineField, PhotoPlace>> = {};
  if (isRecord(raw.places)) {
    for (const field of MACHINE_FIELDS) {
      const place = toPlace(raw.places[field]);
      if (place) {
        places[field] = place;
      }
    }
  }
  const machine: PhotoMachineRead = {
    make: text(raw.make, 60),
    type: text(raw.type, 60),
    serial_raw: text(raw.serial_raw ?? raw.serial, 60),
    serial_norm: text(raw.serial_norm, 60),
    year: text(raw.year, 4),
    statuses,
    place: toPlace(raw.place),
    places,
    brand_hit: isRecord(raw.brand_hit)
      ? { brand: text(raw.brand_hit.brand, 40), machine_type: text(raw.brand_hit.machine_type, 40) }
      : null,
  };
  return machine.make || machine.type || machine.serial_raw || machine.year ? machine : null;
}

/** References as a list (`[{field, text, status}]`) or as the schema's object of fields. */
function toReferences(raw: unknown): PhotoReferenceRead[] {
  const entries: unknown[] = Array.isArray(raw)
    ? raw
    : isRecord(raw)
      ? Object.entries(raw).map(([field, value]) =>
          isRecord(value) ? { ...value, field } : { field, text: value },
        )
      : [];
  const out: PhotoReferenceRead[] = [];
  for (const entry of entries) {
    if (!isRecord(entry)) {
      continue;
    }
    const field = oneOf(REFERENCE_FIELDS, entry.field);
    const value = text(entry.text ?? entry.value, 60);
    if (!field || !value) {
      continue;
    }
    out.push({
      field,
      text: value,
      // A reference without a status was read once at most.
      status: value.includes("?") ? "unclear" : (oneOf(STATUSES, entry.status) ?? "read_once"),
      confidence: unit(entry.confidence),
      place: toPlace(entry.place) ?? toPlace(entry),
    });
    if (out.length >= MAX_REFERENCES) {
      break;
    }
  }
  return out;
}

/** The `photo` block of an attachment; null when the server sent none or nothing usable. */
export function toPhotoReading(raw: unknown): PhotoReading | null {
  if (!isRecord(raw)) {
    return null;
  }
  const items = (Array.isArray(raw.items) ? raw.items : [])
    .map(toItem)
    .filter((item): item is PhotoItemRead => item !== null)
    .slice(0, MAX_PHOTO_ITEMS);
  const fedLines = Array.isArray(raw.fed_lines)
    ? [...new Set(raw.fed_lines.map((n) => ordinal(n)).filter((n): n is number => n !== null))]
    : [];
  return {
    status: oneOf(READ_STATES, raw.status),
    kind: oneOf(KINDS, raw.kind) ?? "other",
    // Unknown means "maybe": no preview unless the server said there is nobody.
    people_visible: raw.people_visible !== false,
    language: text(raw.language, 8),
    parts: ordinal(raw.parts, 16),
    items,
    machine: toMachine(raw.machine),
    references: toReferences(raw.references),
    dropped: count(raw.dropped),
    disagreements: count(raw.disagreements),
    fed_lines: fedLines.sort((a, b) => a - b),
    notes: text(raw.notes),
    injection_suspected: raw.injection_suspected === true,
    check_missing: raw.check_missing === true,
    cache: raw.cache === "hit" || raw.cache === "miss" ? raw.cache : null,
    prompt_version: text(raw.prompt_version, 40),
    filter_category: text(raw.filter_category, 40),
  };
}

/** A reading that gave no values: refused by the content filter or failed. */
export function photoWasRead(photo: PhotoReading | null | undefined): photo is PhotoReading {
  return photo != null && photo.status !== "rejected" && photo.status !== "failed";
}

// ─── which attachments are pictures ────────────────────────────────────────

/** A loose picture (jpg, png, tiff, heic, ...), not a scanned PDF. */
export function isPictureAttachment(attachment: AttachmentRead | null | undefined): boolean {
  return attachment?.kind === "image";
}

/** HEIC / HEIF by the file name: not read yet (§1.2, §7 D3). */
export function isHeic(file: string | null | undefined): boolean {
  return typeof file === "string" && /\.(heic|heif)$/i.test(file.trim());
}

/** Notes of a picture that sales should know about on the panel (decorations are not). */
const PANEL_NOTES = new Set([
  "format_not_supported",
  "heic_not_supported",
  "model_rejected",
  "budget",
  // 14 Oct 2026: the daily cap for reading pictures is reached; the case goes on.
  "daily_limit",
  "too_large",
  "unreadable",
  "seen_in_thread",
  "check_missing",
  "lines_cut",
  "image_unclear",
  // Hunt round 2: a picture read with no part and no quantity says so on the page.
  "no_request",
  // Decision D2 (19 Oct 2026): a reply's picture in the quoted mail only, not read again.
  "quoted_picture",
]);

/**
 * The attachments the "From the photo" panel shows: every picture or scan with a reading,
 * and every picture that was not read for a reason sales should know.
 */
export function photoPanelAttachments(attachments: readonly AttachmentRead[]): AttachmentRead[] {
  return attachments.filter(
    (attachment) =>
      attachment.photo != null ||
      (isPictureAttachment(attachment) &&
        attachment.note != null &&
        PANEL_NOTES.has(attachment.note)),
  );
}

// ─── the rows of the panel ─────────────────────────────────────────────────

/** One value read from a picture, as the panel shows it. */
export interface PhotoRow {
  id: string;
  what: string;
  readAs: string;
  /** Description, "marked on the page", or the server's sentence. */
  detail: string | null;
  status: PhotoReadStatus;
  /** 0..1, never above the status's own figure. */
  confidence: number;
  usedFor: string;
  line: number | null;
  place: PhotoPlace | null;
  /**
   * Decision D2 (19 Oct 2026): the row is from the picture, but the mail text does not
   * ask for it. Context only: never a line, never priced.
   */
  notAsked: boolean;
}

/** The server's figure, capped at the status's own (an unclear value is never 80%). */
function confidenceOf(status: PhotoReadStatus, raw: number | null | undefined): number {
  const cap = STATUS_CONFIDENCE[status];
  return typeof raw === "number" && Number.isFinite(raw) && raw >= 0 ? Math.min(raw, cap) : cap;
}

/**
 * True when a value of the reading is not sure: unclear, left out or read differently.
 * A row the mail does not ask for (D2) does not count: it is context, not the request.
 */
export function photoIsPartly(photo: PhotoReading): boolean {
  if (photo.dropped > 0 || photo.disagreements > 0 || photo.check_missing) {
    return true;
  }
  const statuses = [
    ...photo.items.filter((item) => item.use !== "not_asked").map((item) => item.status),
    ...photo.references.map((ref) => ref.status),
    ...Object.values(photo.machine?.statuses ?? {}),
  ];
  return statuses.includes("unclear");
}

/**
 * The rows of one reading, in the order sales checks them: item numbers and their
 * quantities, then machine details, then references. `lineText(n)` is what draft line n
 * shows ("0990041 NOZZLE 12 MM"), when known.
 */
export function photoRows(
  photo: PhotoReading,
  lineText: (line: number) => string | null = () => null,
): PhotoRow[] {
  const rows: PhotoRow[] = [];
  photo.items.forEach((item, index) => {
    // A row the mail does not ask for never feeds a line, whatever else the wire says.
    const notAsked = item.use === "not_asked";
    const line = notAsked ? null : (item.line ?? null);
    const lineShown = line !== null ? lineText(line) : null;
    const details = [
      item.description,
      item.position ? `position ${item.position}` : null,
      item.marked ? "marked on the page" : null,
    ].filter(Boolean);
    if (item.code) {
      rows.push({
        id: `item-${index}-code`,
        what: PHOTO_FIELD_LABELS.code,
        readAs: item.code,
        detail: item.sentence ?? (details.length > 0 ? details.join(", ") : null),
        status: item.status,
        confidence: confidenceOf(item.status, item.confidence),
        usedFor: photoUsedForText(
          item.status === "unclear" && item.use == null ? "not_looked_up" : item.use,
          line,
          item.status,
          lineShown,
        ),
        line,
        place: item.place ?? null,
        notAsked,
      });
    } else if (item.description) {
      rows.push({
        id: `item-${index}-description`,
        what: PHOTO_FIELD_LABELS.description,
        readAs: item.description,
        detail: item.marked ? "marked on the page" : null,
        status: item.status,
        confidence: confidenceOf(item.status, item.confidence),
        usedFor: photoUsedForText(item.use, line, item.status, lineShown),
        line,
        place: item.place ?? null,
        notAsked,
      });
    }
    if (item.qty != null) {
      const status = item.qty_status ?? "read_once";
      rows.push({
        id: `item-${index}-qty`,
        what: PHOTO_FIELD_LABELS.qty,
        readAs: [String(item.qty), item.unit].filter(Boolean).join(" "),
        detail: null,
        status,
        confidence: STATUS_CONFIDENCE[status],
        usedFor: photoUsedForText(
          notAsked ? "not_asked" : line !== null ? null : "no_line",
          line,
          status,
        ),
        line,
        place: item.place ?? null,
        notAsked,
      });
    }
  });

  const machine = photo.machine;
  if (machine) {
    const values: [PhotoMachineField, string | number | null | undefined][] = [
      ["make", machine.make],
      ["type", machine.type],
      ["serial", machine.serial_raw],
      ["year", machine.year],
    ];
    for (const [field, value] of values) {
      if (value == null || value === "") {
        continue;
      }
      // A field without a status was read once at most.
      const status = machine.statuses?.[field] ?? "read_once";
      rows.push({
        id: `machine-${field}`,
        what: PHOTO_FIELD_LABELS[field],
        readAs: String(value),
        detail:
          field === "serial" && machine.serial_norm && machine.serial_norm !== machine.serial_raw
            ? `searched as ${machine.serial_norm}`
            : null,
        status,
        confidence: STATUS_CONFIDENCE[status],
        usedFor: PHOTO_MACHINE_USE,
        line: null,
        place: machine.places?.[field] ?? machine.place ?? null,
        notAsked: false,
      });
    }
  }

  photo.references.forEach((ref) => {
    rows.push({
      id: `ref-${ref.field}`,
      what: PHOTO_FIELD_LABELS[ref.field],
      readAs: ref.text,
      detail: null,
      status: ref.status,
      confidence: confidenceOf(ref.status, ref.confidence),
      usedFor: ref.field === "sales_order" ? PHOTO_SALES_ORDER_USE : PHOTO_REFERENCE_USE,
      line: null,
      place: ref.place ?? null,
      notAsked: false,
    });
  });
  return rows;
}

// ─── the frame inside a part ───────────────────────────────────────────────

/**
 * The approximate frame of a value inside the shown image, 0..1 of that image. With a
 * part, the model's box (of the whole picture) is moved into the part's rectangle and
 * shown only when it lies inside it (§2.4 point 4); without a part, on the whole picture.
 */
export function frameInView(
  place: PhotoPlace | null | undefined,
): [number, number, number, number] | null {
  const box = toBox(place?.box);
  if (!box) {
    return null;
  }
  if (place?.part == null) {
    return box;
  }
  const part = toBox(place.part_box);
  if (!part) {
    return null;
  }
  const [px0, py0, px1, py1] = part;
  const [x0, y0, x1, y1] = box;
  const slack = 1e-6;
  if (x0 < px0 - slack || y0 < py0 - slack || x1 > px1 + slack || y1 > py1 + slack) {
    return null;
  }
  const w = px1 - px0;
  const h = py1 - py0;
  const clamp = (v: number) => Math.min(1, Math.max(0, v));
  return [
    clamp((x0 - px0) / w),
    clamp((y0 - py0) / h),
    clamp((x1 - px0) / w),
    clamp((y1 - py0) / h),
  ];
}
